import type { ReactElement } from 'react'
import { Trophy, Medal } from 'lucide-react'
import type {
  BracketMatch as BracketMatchType,
  BracketParticipant,
  BracketPlan,
  BracketPrelim as BracketPrelimType,
  BracketSlot as BracketSlotType
} from './types'

/** A match accepts a result tap when persisted and with both sides resolved
 * to concrete teams (directly seeded or filled by propagation). */
export function isMatchTappable(match: { id?: string | null; top: BracketSlotType; bottom: BracketSlotType }): boolean {
  return Boolean(match.id && match.top.team && match.bottom.team)
}

/** Score cell for one side of a decided match ("18", "W.O." for the walkover winner). */
export function ScoreBadge({
  score,
  walkover,
  isWinner
}: {
  score?: number | null
  walkover?: boolean
  isWinner: boolean
}) {
  const label = walkover ? (isWinner ? 'W.O.' : '—') : score
  if (label == null) return null
  return (
    <span
      className={`ml-1 text-xs tabular-nums flex-shrink-0 ${
        isWinner ? 'font-semibold text-gray-900' : 'text-gray-400'
      }`}
    >
      {label}
    </span>
  )
}

// A team-holding position in the plan (prelim side or main first-round slot).
type TeamPos =
  | { area: 'prelim'; index: number; side: 'top' | 'bottom' }
  | { area: 'round0'; matchIndex: number; side: 'top' | 'bottom' }

const posKey = (pos: TeamPos): string =>
  pos.area === 'prelim'
    ? `prelim:${pos.index}:${pos.side}`
    : `round0:${pos.matchIndex}:${pos.side}`

/** Every position that holds a real team, in a stable order. */
export function listTeamPositions(plan: BracketPlan): TeamPos[] {
  const positions: TeamPos[] = []
  plan.prelims.forEach((_, index) => {
    positions.push({ area: 'prelim', index, side: 'top' })
    positions.push({ area: 'prelim', index, side: 'bottom' })
  })
  plan.rounds[0]?.matches.forEach((match, matchIndex) => {
    if (match.top.kind === 'team') positions.push({ area: 'round0', matchIndex, side: 'top' })
    if (match.bottom.kind === 'team') positions.push({ area: 'round0', matchIndex, side: 'bottom' })
  })
  return positions
}

function participantAt(plan: BracketPlan, pos: TeamPos): BracketParticipant {
  return pos.area === 'prelim'
    ? plan.prelims[pos.index][pos.side]
    : (plan.rounds[0].matches[pos.matchIndex][pos.side].team as BracketParticipant)
}

function setParticipant(plan: BracketPlan, pos: TeamPos, participant: BracketParticipant) {
  if (pos.area === 'prelim') {
    plan.prelims[pos.index][pos.side] = participant
  } else {
    plan.rounds[0].matches[pos.matchIndex][pos.side].team = participant
  }
}

/**
 * Swaps two teams in place. Only which team sits in which slot changes — the
 * bracket shape is fixed by the field size — so no backend call is needed.
 */
export function swapTeams(plan: BracketPlan, i: number, j: number): BracketPlan {
  const clone = structuredClone(plan)
  const positions = listTeamPositions(clone)
  const a = positions[i]
  const b = positions[j]
  const pa = participantAt(clone, a)
  const pb = participantAt(clone, b)
  setParticipant(clone, a, pb)
  setParticipant(clone, b, pa)
  return clone
}

/** Final team order (team ids), in the same order the generator consumes. */
export function seedOrder(plan: BracketPlan): string[] {
  return listTeamPositions(plan).map((pos) => participantAt(plan, pos).id)
}

/** Display label for a round, derived from its identifier (roundOf). */
function roundLabel(roundOf: number, index: number): string {
  switch (roundOf) {
    case 2:
      return 'Final'
    case 4:
      return 'Semifinais'
    case 8:
      return 'Quartas'
    case 16:
      return 'Oitavas'
    default:
      return `Rodada ${index + 1}`
  }
}

export const ROW = 128
export const GAP_HALF = 24
export const LINE_OFFSET = 20
export const LINE = '#B4B2A9'
/** Minimum vertical distance between preliminary cards (card + breathing room). */
export const PRELIM_SPACING = 104

/** Card centers for the whole tree, computed as a tree: every first-round
 * match reserves a vertical band big enough for the preliminaries that feed it
 * and centers on them; every later match centers between its two feeders. The
 * geometry uses only these fixed constants — it never depends on the screen. */
export interface BracketLayout {
  /** prelim number → card center y */
  prelimYs: Map<number, number>
  /** prelim number → y of the first-round slot it feeds */
  slotYs: Map<number, number>
  /** [roundIdx][matchIdx] → card center y */
  roundYs: number[][]
  height: number
}

export function layoutBracket(plan: BracketPlan): BracketLayout {
  const prelimYs = new Map<number, number>()
  const slotYs = new Map<number, number>()
  const roundYs: number[][] = []
  const round0 = plan.rounds[0]?.matches ?? []

  // First round: each match takes a band — ROW alone, or the room its
  // preliminaries need. Prelims sit inside the band; the match centers on them.
  const ys0: number[] = []
  let cursor = 0
  round0.forEach((match) => {
    const feeders: number[] = []
    if (match.top.kind === 'prelim_winner' && match.top.prelimNumber != null) {
      feeders.push(match.top.prelimNumber)
    }
    if (match.bottom.kind === 'prelim_winner' && match.bottom.prelimNumber != null) {
      feeders.push(match.bottom.prelimNumber)
    }
    const band = Math.max(ROW, feeders.length * PRELIM_SPACING)
    if (feeders.length === 0) {
      ys0.push(cursor + band / 2)
    } else {
      const first = cursor + band / 2 - ((feeders.length - 1) * PRELIM_SPACING) / 2
      feeders.forEach((number, k) => prelimYs.set(number, first + k * PRELIM_SPACING))
      const sum = feeders.reduce((acc, number) => acc + (prelimYs.get(number) ?? 0), 0)
      ys0.push(sum / feeders.length)
    }
    cursor += band
  })
  roundYs.push(ys0)

  // Any prelim not referenced by a first-round slot stacks below (defensive).
  for (const prelim of [...plan.prelims].sort((a, b) => a.number - b.number)) {
    if (!prelimYs.has(prelim.number)) {
      prelimYs.set(prelim.number, cursor + PRELIM_SPACING / 2)
      cursor += PRELIM_SPACING
    }
  }

  // Where each prelim's winner lands (connector endpoint on the match card).
  round0.forEach((match, i) => {
    if (match.top.kind === 'prelim_winner' && match.top.prelimNumber != null) {
      slotYs.set(match.top.prelimNumber, ys0[i] - LINE_OFFSET)
    }
    if (match.bottom.kind === 'prelim_winner' && match.bottom.prelimNumber != null) {
      slotYs.set(match.bottom.prelimNumber, ys0[i] + LINE_OFFSET)
    }
  })

  // Later rounds: centered between the two feeding matches (consecutive pairs).
  for (let r = 1; r < plan.rounds.length; r++) {
    const prev = roundYs[r - 1]
    roundYs.push(
      plan.rounds[r].matches.map((_, i) => {
        const a = prev[2 * i]
        const b = prev[2 * i + 1]
        if (a == null) return ROW / 2
        return b == null ? a : (a + b) / 2
      })
    )
  }

  return { prelimYs, slotYs, roundYs, height: Math.max(cursor, ROW) }
}

/** Prefix for match references, by section. References without a section point
 * inside the same bracket — they inherit its own prefix (J by default; the
 * double-elimination final stage uses F to not collide with qualifying J#s). */
function sectionPrefix(section: string | null | undefined, ownPrefix: string): string {
  switch (section) {
    case 'preliminary':
      return 'P'
    case 'winners':
      return 'J'
    case 'losers':
      return 'L'
    case 'main':
      return 'F'
    default:
      return ownPrefix
  }
}

/** Normalizes a preliminary into the match shape (result modal, tap checks). */
export function prelimToMatch(prelim: BracketPrelimType): BracketMatchType {
  return {
    id: prelim.id,
    number: prelim.number,
    top: { kind: 'team', team: prelim.top },
    bottom: { kind: 'team', team: prelim.bottom },
    topScore: prelim.topScore,
    bottomScore: prelim.bottomScore,
    winnerTeamId: prelim.winnerTeamId,
    walkover: prelim.walkover
  }
}

/** The tournament champion's name, when the final is decided. */
export function championName(plan: BracketPlan): string | null {
  const final = plan.rounds[plan.rounds.length - 1]?.matches[0]
  if (!final?.winnerTeamId) return null
  for (const slot of [final.top, final.bottom]) {
    if (slot.team?.id === final.winnerTeamId) return slot.team.name
  }
  return null
}

/** 1 → "A", 2 → "B" … (falls back to the number past "Z"). */
function groupLetter(number: number): string {
  return number >= 1 && number <= 26 ? String.fromCharCode(64 + number) : String(number)
}

/** Human label for a non-team slot ("Vencedor J3", "Perdedor L2", "2º Grupo B"...). */
export function slotRefLabel(slot: BracketSlotType, ownPrefix = 'J'): string {
  if (slot.kind === 'prelim_winner') return `Vencedor P${slot.prelimNumber}`
  if (slot.kind === 'group_place' && slot.groupNumber != null && slot.groupPosition != null) {
    return `${slot.groupPosition}º Grupo ${groupLetter(slot.groupNumber)}`
  }
  const prefix = sectionPrefix(slot.matchSection, ownPrefix)
  if (slot.kind === 'match_winner') return `Vencedor ${prefix}${slot.matchNumber}`
  if (slot.kind === 'match_loser') return `Perdedor ${prefix}${slot.matchNumber}`
  return '—'
}

interface BracketTreeProps {
  plan: BracketPlan
  interactive: boolean
  selected: number | null
  onTap: (posIdx: number) => void
  /** Hide the champion column (double elimination continues to the grand final). */
  showChampion?: boolean
  /** When false, the tree doesn't scroll itself — a composing parent does. */
  scroll?: boolean
  /** Overrides round headers (the double-elimination qualifying brackets use
   * numbered rounds instead of knockout names). */
  roundLabelFn?: (roundOf: number, index: number) => string
  /** Display prefix for this bracket's match numbers (J by default; the
   * double-elimination final stage uses F). */
  matchPrefix?: string
  /** Result launching: called when a tappable match is clicked. */
  onMatchTap?: (match: BracketMatchType) => void
}

/** Renders a single-elimination-shaped bracket: preliminary column, main rounds, champion. */
export function BracketTree({
  plan,
  interactive,
  selected,
  onTap,
  showChampion = true,
  scroll = true,
  roundLabelFn,
  matchPrefix = 'J',
  onMatchTap
}: BracketTreeProps) {
  const prelimCount = plan.prelims.length

  const positions = listTeamPositions(plan)
  const posIndex = new Map<string, number>()
  positions.forEach((pos, i) => posIndex.set(posKey(pos), i))

  const layout = layoutBracket(plan)
  const finalY = layout.roundYs[plan.rounds.length - 1]?.[0] ?? ROW / 2
  // The third-place card hangs below the final; give the canvas room for it.
  const height = plan.thirdPlace ? Math.max(layout.height, finalY + 160) : layout.height

  const teamSlot = (pos: TeamPos, participant: BracketParticipant) => {
    const idx = posIndex.get(posKey(pos)) ?? -1
    return (
      <TeamSlot
        name={participant.name}
        posIdx={idx}
        interactive={interactive}
        selected={selected === idx}
        onTap={onTap}
      />
    )
  }

  return (
    // The bracket area fills the remaining screen height and is the only
    // scroller (both axes) — the lateral scrollbar sits at the bottom of the
    // screen, and the page itself never scrolls sideways. Composed views
    // (double elimination) scroll at their own level instead.
    <div className={scroll ? 'h-full w-full overflow-auto pb-2 pr-10' : 'w-max'}>
      <div className="flex items-start" style={{ gap: GAP_HALF * 2, minWidth: 'max-content' }}>
        {prelimCount > 0 && (
          <div className="flex-shrink-0" style={{ width: 184 }}>
            <div className="text-xs font-semibold text-gray-500 uppercase mb-2 text-center h-4">
              Preliminar
            </div>
            <div className="relative" style={{ height }}>
              {plan.prelims.map((prelim, k) => {
                const cardY = layout.prelimYs.get(prelim.number) ?? PRELIM_SPACING / 2
                const slotY = layout.slotYs.get(prelim.number) ?? cardY
                return (
                  <div key={prelim.number}>
                    {/* Out of the card, then vertically to the slot it feeds
                        (cards keep their spacing, so they may sit off-line). */}
                    <span
                      className="absolute h-px"
                      style={{ top: cardY, right: -GAP_HALF, width: GAP_HALF, background: LINE }}
                    />
                    {Math.abs(slotY - cardY) > 1 && (
                      <span
                        className="absolute w-px"
                        style={{
                          right: -GAP_HALF,
                          top: Math.min(cardY, slotY),
                          height: Math.abs(slotY - cardY),
                          background: LINE
                        }}
                      />
                    )}
                    <div
                      className="absolute left-0 right-0"
                      style={{ top: cardY, transform: 'translateY(-50%)' }}
                    >
                      <div
                        onClick={
                          onMatchTap && isMatchTappable(prelimToMatch(prelim))
                            ? () => onMatchTap(prelimToMatch(prelim))
                            : undefined
                        }
                        className={`bg-white rounded-lg border border-gray-200 px-2 py-1 shadow-sm text-xs leading-tight ${
                          onMatchTap && isMatchTappable(prelimToMatch(prelim))
                            ? 'cursor-pointer transition-colors hover:border-brand-primary/60'
                            : ''
                        }`}
                      >
                        <div className="text-[10px] font-semibold text-gray-400 mb-0.5">
                          Prelim P{prelim.number}
                        </div>
                        <div className="flex items-center">
                          <div className="flex-1 min-w-0">
                            {prelim.winnerTeamId ? (
                              <div
                                className={`truncate ${
                                  prelim.top.id === prelim.winnerTeamId
                                    ? 'font-semibold text-gray-900'
                                    : 'text-gray-400'
                                }`}
                              >
                                {prelim.top.name}
                              </div>
                            ) : (
                              teamSlot({ area: 'prelim', index: k, side: 'top' }, prelim.top)
                            )}
                          </div>
                          <ScoreBadge
                            score={prelim.topScore}
                            walkover={prelim.walkover}
                            isWinner={prelim.winnerTeamId != null && prelim.top.id === prelim.winnerTeamId}
                          />
                        </div>
                        <div className="border-t border-gray-100 my-0.5" />
                        <div className="flex items-center">
                          <div className="flex-1 min-w-0">
                            {prelim.winnerTeamId ? (
                              <div
                                className={`truncate ${
                                  prelim.bottom.id === prelim.winnerTeamId
                                    ? 'font-semibold text-gray-900'
                                    : 'text-gray-400'
                                }`}
                              >
                                {prelim.bottom.name}
                              </div>
                            ) : (
                              teamSlot({ area: 'prelim', index: k, side: 'bottom' }, prelim.bottom)
                            )}
                          </div>
                          <ScoreBadge
                            score={prelim.bottomScore}
                            walkover={prelim.walkover}
                            isWinner={prelim.winnerTeamId != null && prelim.bottom.id === prelim.winnerTeamId}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {plan.rounds.map((round, roundIdx) => {
          const isFinal = roundIdx === plan.rounds.length - 1
          return (
            <div key={roundIdx} className="flex-shrink-0" style={{ width: 208 }}>
              <div className="text-xs font-semibold text-gray-500 uppercase mb-2 text-center h-4">
                {(roundLabelFn ?? roundLabel)(round.roundOf, roundIdx)}
              </div>
              <div className="relative" style={{ height }}>
                {round.matches.map((match, matchIdx) => {
                  const y = layout.roundYs[roundIdx][matchIdx]
                  const feederA = roundIdx > 0 ? layout.roundYs[roundIdx - 1][2 * matchIdx] : null
                  const feederB = roundIdx > 0 ? layout.roundYs[roundIdx - 1][2 * matchIdx + 1] : null
                  return (
                    <div key={match.number}>
                      {/* Joins the two feeding matches, then enters this card. */}
                      {feederA != null && feederB != null && (
                        <span
                          className="absolute w-px"
                          style={{ left: -GAP_HALF, top: feederA, height: feederB - feederA, background: LINE }}
                        />
                      )}
                      {roundIdx > 0 && (
                        <span
                          className="absolute h-px"
                          style={{ left: -GAP_HALF, width: GAP_HALF, top: y, background: LINE }}
                        />
                      )}
                      {roundIdx === 0 && match.top.kind === 'prelim_winner' && (
                        <span
                          className="absolute h-px"
                          style={{ left: -GAP_HALF, width: GAP_HALF, top: y - LINE_OFFSET, background: LINE }}
                        />
                      )}
                      {roundIdx === 0 && match.bottom.kind === 'prelim_winner' && (
                        <span
                          className="absolute h-px"
                          style={{ left: -GAP_HALF, width: GAP_HALF, top: y + LINE_OFFSET, background: LINE }}
                        />
                      )}
                      {!isFinal && (
                        <span
                          className="absolute h-px"
                          style={{ right: -GAP_HALF, width: GAP_HALF, top: y, background: LINE }}
                        />
                      )}

                      <div
                        onClick={onMatchTap && isMatchTappable(match) ? () => onMatchTap(match) : undefined}
                        className={`absolute left-0 right-0 bg-white rounded-lg border border-gray-200 px-2 py-1.5 text-sm shadow-sm ${
                          onMatchTap && isMatchTappable(match)
                            ? 'cursor-pointer transition-colors hover:border-brand-primary/60'
                            : ''
                        }`}
                        style={{ top: y, transform: 'translateY(-50%)' }}
                      >
                        <div className="text-[10px] font-semibold text-gray-400 mb-0.5">
                          {matchPrefix}{match.number}
                        </div>
                        <div className="flex items-center">
                          <div className="flex-1 min-w-0">
                            <SlotLine
                              slot={match.top}
                              position={roundIdx === 0 ? { area: 'round0', matchIndex: matchIdx, side: 'top' } : null}
                              teamSlot={teamSlot}
                              ownPrefix={matchPrefix}
                              winnerTeamId={match.winnerTeamId}
                            />
                          </div>
                          <ScoreBadge
                            score={match.topScore}
                            walkover={match.walkover}
                            isWinner={match.winnerTeamId != null && match.top.team?.id === match.winnerTeamId}
                          />
                        </div>
                        <div className="border-t border-gray-100 my-0.5" />
                        <div className="flex items-center">
                          <div className="flex-1 min-w-0">
                            <SlotLine
                              slot={match.bottom}
                              position={
                                roundIdx === 0 ? { area: 'round0', matchIndex: matchIdx, side: 'bottom' } : null
                              }
                              teamSlot={teamSlot}
                              ownPrefix={matchPrefix}
                              winnerTeamId={match.winnerTeamId}
                            />
                          </div>
                          <ScoreBadge
                            score={match.bottomScore}
                            walkover={match.walkover}
                            isWinner={match.winnerTeamId != null && match.bottom.team?.id === match.winnerTeamId}
                          />
                        </div>
                      </div>

                      {isFinal && plan.thirdPlace && (
                        <div
                          onClick={
                            onMatchTap && isMatchTappable(plan.thirdPlace)
                              ? () => onMatchTap(plan.thirdPlace!)
                              : undefined
                          }
                          className={`absolute left-0 right-0 bg-white rounded-lg border border-dashed border-gray-300 px-2 py-1.5 ${
                            onMatchTap && isMatchTappable(plan.thirdPlace)
                              ? 'cursor-pointer transition-colors hover:border-brand-primary/60'
                              : ''
                          }`}
                          style={{ top: y + LINE_OFFSET * 2 + 12 }}
                        >
                          <div className="flex items-center gap-1 text-[10px] font-semibold text-amber-600 mb-0.5">
                            <Medal className="w-3 h-3" />3º LUGAR
                          </div>
                          <div className="flex items-center text-sm">
                            <div className="flex-1 min-w-0">
                              <SlotLine
                                slot={plan.thirdPlace.top}
                                position={null}
                                teamSlot={teamSlot}
                                ownPrefix={matchPrefix}
                                winnerTeamId={plan.thirdPlace.winnerTeamId}
                              />
                            </div>
                            <ScoreBadge
                              score={plan.thirdPlace.topScore}
                              walkover={plan.thirdPlace.walkover}
                              isWinner={
                                plan.thirdPlace.winnerTeamId != null &&
                                plan.thirdPlace.top.team?.id === plan.thirdPlace.winnerTeamId
                              }
                            />
                          </div>
                          <div className="border-t border-gray-100 my-0.5" />
                          <div className="flex items-center text-sm">
                            <div className="flex-1 min-w-0">
                              <SlotLine
                                slot={plan.thirdPlace.bottom}
                                position={null}
                                teamSlot={teamSlot}
                                ownPrefix={matchPrefix}
                                winnerTeamId={plan.thirdPlace.winnerTeamId}
                              />
                            </div>
                            <ScoreBadge
                              score={plan.thirdPlace.bottomScore}
                              walkover={plan.thirdPlace.walkover}
                              isWinner={
                                plan.thirdPlace.winnerTeamId != null &&
                                plan.thirdPlace.bottom.team?.id === plan.thirdPlace.winnerTeamId
                              }
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}

        {showChampion && (
          <div className="flex-shrink-0" style={{ width: 176 }}>
            <div className="text-xs font-semibold text-gray-500 uppercase mb-2 text-center h-4">
              Campeão
            </div>
            <div className="relative" style={{ height }}>
              <div
                className="absolute left-0 right-0"
                style={{ top: finalY, transform: 'translateY(-50%)' }}
              >
                <div className="relative w-full">
                  <span
                    className="absolute top-1/2 h-px"
                    style={{ left: -GAP_HALF, width: GAP_HALF, background: LINE }}
                  />
                  <div className="w-full bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-center gap-2">
                    <Trophy className="w-4 h-4 text-amber-500 flex-shrink-0" />
                    <span className="text-sm text-amber-700">Vencedor da final</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function SlotLine({
  slot,
  position,
  teamSlot,
  ownPrefix,
  winnerTeamId
}: {
  slot: BracketSlotType
  position: TeamPos | null
  teamSlot: (pos: TeamPos, participant: BracketParticipant) => ReactElement
  ownPrefix: string
  winnerTeamId?: string | null
}) {
  // A resolved slot shows its team regardless of kind — reference kinds keep
  // the structure and carry the propagated team once the source is decided.
  if (slot.team) {
    // Decided match: emphasize the winner, mute the loser (no swaps here).
    if (winnerTeamId) {
      const isWinner = slot.team.id === winnerTeamId
      return (
        <div className={`truncate ${isWinner ? 'font-semibold text-gray-900' : 'text-gray-400'}`}>
          {slot.team.name}
        </div>
      )
    }
    // Swaps only apply to directly seeded teams (draft-time positions).
    if (slot.kind === 'team' && position) return teamSlot(position, slot.team)
    return <div className="text-gray-900 truncate">{slot.team.name}</div>
  }
  return <div className="text-gray-400 truncate">{slotRefLabel(slot, ownPrefix)}</div>
}

function TeamSlot({
  name,
  posIdx,
  interactive,
  selected,
  onTap
}: {
  name: string
  posIdx: number
  interactive: boolean
  selected: boolean
  onTap: (posIdx: number) => void
}) {
  if (!interactive) {
    return <div className="text-gray-900 truncate">{name}</div>
  }
  return (
    <button
      type="button"
      onClick={() => onTap(posIdx)}
      className={`block w-full text-left truncate rounded px-1 -mx-1 transition-colors ${
        selected
          ? 'bg-brand-primary/10 text-brand-primary font-medium'
          : 'text-gray-900 hover:bg-gray-100'
      }`}
    >
      {name}
    </button>
  )
}

export function BracketEmptyCard({ title, message }: { title: string; message: string }) {
  return (
    <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <Trophy className="w-8 h-8 text-gray-400" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600">{message}</p>
    </div>
  )
}
